import axios from "axios";

import {
  KillHistoryResultType,
  MOCK_KILL_HISTORY_ENABLED,
} from "../constants/killHistory";
import { SERVER_URL } from "../config/config";
import { DeadAccountKillerInfo } from "../types/DeadAccountKillerInfo";
import { KillHistoryEntry } from "../types/KillHistoryEntry";
import { ProfileNames } from "../types/ProfileName";
import {
  fetchKillHistoryEntries,
  getKillHistoryResultLabel,
} from "./killHistory";

const hoursAgo = (hours: number): number =>
  Math.floor(Date.now() / 1000) - hours * 3600;

/** Fallback killer shown in mock mode when no history row matches the victim. */
export const MOCK_FALLBACK_DEAD_ACCOUNT_KILLER: DeadAccountKillerInfo = {
  killerName: "AldoVescari",
  killerAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
  timestamp: hoursAgo(3),
  resultType: KillHistoryResultType.SingleKill,
};

const mapKillEntryToKillerInfo = (
  entry: KillHistoryEntry
): DeadAccountKillerInfo => ({
  killerName:
    entry.attackerName ?? entry.attackerAddress,
  killerAddress: entry.attackerAddress,
  killerWords: entry.killerWords,
  timestamp: entry.timestamp,
  resultType: entry.resultType,
});

const victimMatchesEntry = (
  entry: KillHistoryEntry,
  victimAddress?: string,
  victimName?: string
): boolean => {
  if (
    victimName &&
    entry.victimName?.toLowerCase() === victimName.toLowerCase()
  ) {
    return true;
  }

  if (
    victimAddress &&
    entry.victimAddress.toLowerCase() === victimAddress.toLowerCase()
  ) {
    return true;
  }

  return false;
};

const findKillerFromHistory = (
  entries: KillHistoryEntry[],
  victimAddress?: string,
  victimName?: string
): DeadAccountKillerInfo | null => {
  const match = entries.find((entry) =>
    victimMatchesEntry(entry, victimAddress, victimName)
  );

  return match ? mapKillEntryToKillerInfo(match) : null;
};

const normalizeApiKillerInfo = (
  raw: Record<string, unknown>
): DeadAccountKillerInfo | null => {
  const killerName = String(
    raw.killerName ??
      raw.killer_name ??
      raw.attackerName ??
      raw.attacker_name ??
      ""
  );

  if (!killerName) {
    return null;
  }

  const resultRaw =
    raw.resultType ?? raw.result_type ?? raw.result ?? raw.killType;
  let resultType = KillHistoryResultType.SingleKill;

  if (typeof resultRaw === "string") {
    const normalized = resultRaw.toLowerCase();
    if (normalized.includes("double")) {
      resultType = KillHistoryResultType.DoubleKill;
    } else if (normalized.includes("backfire")) {
      resultType = KillHistoryResultType.BackfireKill;
    }
  } else {
    const numeric = Number(resultRaw);
    if (numeric === KillHistoryResultType.DoubleKill) {
      resultType = KillHistoryResultType.DoubleKill;
    } else if (numeric === KillHistoryResultType.BackfireKill) {
      resultType = KillHistoryResultType.BackfireKill;
    }
  }

  const timestamp = Number(
    raw.timestamp ?? raw.killedAt ?? raw.killed_at ?? 0
  );

  return {
    killerName,
    killerAddress: raw.killerAddress
      ? String(raw.killerAddress)
      : raw.killer_address
        ? String(raw.killer_address)
        : raw.attackerAddress
          ? String(raw.attackerAddress)
          : undefined,
    killerWords: String(
      raw.killerWords ??
        raw.killer_words ??
        raw.lastWords ??
        raw.last_words ??
        raw.message ??
        ""
    ).trim() || undefined,
    timestamp,
    resultType,
  };
};

const fetchKillerFromApi = async (
  victimAddress?: string
): Promise<DeadAccountKillerInfo | null> => {
  if (!victimAddress) {
    return null;
  }

  try {
    const response = await axios.get(
      `${SERVER_URL}/kill/my-death/${victimAddress}`
    );
    const payload = response.data as Record<string, unknown>;

    return normalizeApiKillerInfo(payload);
  } catch {
    return null;
  }
};

export const fetchDeadAccountKillerInfo = async (
  victimAddress: string | undefined,
  victimName: string | undefined,
  profileNames: ProfileNames[]
): Promise<DeadAccountKillerInfo | null> => {
  const historyEntries = await fetchKillHistoryEntries(profileNames);
  const fromHistory = findKillerFromHistory(
    historyEntries,
    victimAddress,
    victimName
  );

  if (fromHistory) {
    return fromHistory;
  }

  const fromApi = await fetchKillerFromApi(victimAddress);
  if (fromApi) {
    return fromApi;
  }

  if (MOCK_KILL_HISTORY_ENABLED) {
    return { ...MOCK_FALLBACK_DEAD_ACCOUNT_KILLER };
  }

  return null;
};

export { getKillHistoryResultLabel };
